import {
  Box,
  Checkbox,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { AlertTriangle, Plus, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "components/ds";
import {
  MF_ACCESS_REVIEWS,
  MFEntitlementOption,
  MFReviewAccount,
} from "state/members/accessReviews";
import {
  applyBulkStates,
  bulkEntitlementStates,
  bulkStateChanged,
  correctionToPayload,
  currentEntitlementIds,
  deriveEntitlementDiff,
  groupOptionsByCategory,
  hasEntitlementChange,
  nextBulkEntState,
  type AccountEntitlements,
  type EntState,
  type StagedCorrection,
} from "../reviewLogic";

interface FixIncorrectDataModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedAccounts: MFReviewAccount[];
  /** Apply the corrections immediately (persists); resolves when done. */
  onApply: (corrections: StagedCorrection[]) => Promise<void>;
  /** The review takes no more changes (its cycle ended): confirm is off. */
  isLocked?: boolean;
}

const accountLabel = (a: MFReviewAccount): string =>
  a.member?.name ||
  a.member?.email ||
  a.identity?.displayName ||
  a.identity?.extId ||
  "this account";

/**
 * FixIncorrectDataModal (PLO-4803 P5) — "Fix incorrect data" for one account or,
 * over a bulk selection, across all of them. The reviewer ticks the entitlements
 * the account(s) actually have at source; Apply STAGES the result as a draft (it
 * does not persist — that happens from the corrections panel).
 *
 * Single-account: a 2-state checklist with "Adding" / "Removing" chips on the
 * changed rows. Bulk: a TRI-STATE checklist — each entitlement starts checked
 * (all have it), unchecked (none) or mixed (some); checked adds to every account,
 * unchecked removes from every account, left mixed leaves each account as-is.
 */
const FixIncorrectDataModal = ({
  isOpen,
  onClose,
  selectedAccounts,
  onApply,
  isLocked = false,
}: FixIncorrectDataModalProps) => {
  const isBulk = selectedAccounts.length > 1;
  const firstAccountId = selectedAccounts[0]?.id || null;
  const { data: entitlementOptions, isLoading: isLoadingOptions } =
    MF_ACCESS_REVIEWS.useGetEntitlementOptions(firstAccountId);

  const [searchQuery, setSearchQuery] = useState("");
  const [newEntitlementNames, setNewEntitlementNames] = useState<string[]>([]);
  // Single-account: the desired existing-entitlement id set.
  const [picked, setPicked] = useState<string[]>([]);
  // Bulk: the per-entitlement tri-state, keyed by option id.
  const [bulkState, setBulkState] = useState<Record<string, EntState>>({});
  const [isApplying, setIsApplying] = useState(false);

  // Name lookup across options + whatever the accounts already reference.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of entitlementOptions) map.set(o.id, o.name);
    for (const a of selectedAccounts) {
      for (const e of a.entitlements || []) {
        if (e.snapshotEntitlementId)
          map.set(e.snapshotEntitlementId, e.snapshotEntitlementName);
      }
    }
    return map;
  }, [entitlementOptions, selectedAccounts]);

  const selections: AccountEntitlements[] = useMemo(
    () =>
      selectedAccounts.map((a) => ({
        accountId: a.id,
        entitlementIds: currentEntitlementIds(a),
      })),
    [selectedAccounts],
  );

  const catalogueIds = useMemo(
    () => entitlementOptions.map((o) => o.id),
    [entitlementOptions],
  );

  const originalBulkState = useMemo(
    () => bulkEntitlementStates(selections, catalogueIds),
    [selections, catalogueIds],
  );

  // Seed state when the modal opens (or its selection/options change).
  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery("");
    setNewEntitlementNames([]);
    if (isBulk) {
      setBulkState(originalBulkState);
    } else if (selectedAccounts[0]) {
      setPicked(currentEntitlementIds(selectedAccounts[0]));
    }
  }, [isOpen, isBulk, originalBulkState, selectedAccounts]);

  const groups = useMemo(
    () => groupOptionsByCategory(entitlementOptions),
    [entitlementOptions],
  );

  const matches = (o: MFEntitlementOption): boolean => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      o.name.toLowerCase().includes(q) ||
      (o.type?.toLowerCase().includes(q) ?? false)
    );
  };

  const hasExactMatch = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return entitlementOptions.some((o) => o.name.toLowerCase() === q);
  }, [searchQuery, entitlementOptions]);

  const toggleSingle = (id: string) =>
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleBulk = (id: string) =>
    setBulkState((prev) => ({
      ...prev,
      [id]: nextBulkEntState(originalBulkState[id], prev[id]),
    }));

  const handleCreateEntitlement = () => {
    const name = searchQuery.trim();
    if (!name || hasExactMatch) return;
    setNewEntitlementNames((prev) =>
      prev.includes(name) ? prev : [...prev, name],
    );
    setSearchQuery("");
  };

  const changed = isBulk
    ? bulkStateChanged(originalBulkState, bulkState) ||
      newEntitlementNames.length > 0
    : hasEntitlementChange(
        currentEntitlementIds(selectedAccounts[0] || ({} as MFReviewAccount)),
        picked,
        newEntitlementNames,
      );

  const stageSingle = (): StagedCorrection[] => {
    const account = selectedAccounts[0];
    if (!account) return [];
    const original = currentEntitlementIds(account);
    const diff = deriveEntitlementDiff(original, picked);
    const payload = correctionToPayload(account.id, picked, newEntitlementNames);
    return [
      {
        ...payload,
        accountLabel: accountLabel(account),
        addedNames: [
          ...diff.added.map((id) => nameById.get(id) ?? id),
          ...payload.newEntitlementNames,
        ],
        removedNames: diff.removed.map((id) => nameById.get(id) ?? id),
      },
    ];
  };

  const stageBulk = (): StagedCorrection[] => {
    const applied = applyBulkStates(selections, bulkState);
    const labelById = new Map(
      selectedAccounts.map((a) => [a.id, accountLabel(a)]),
    );
    const originalById = new Map(
      selections.map((s) => [s.accountId, s.entitlementIds]),
    );
    return applied
      .map(({ accountId, entitlementIds }) => {
        const original = originalById.get(accountId) ?? [];
        const diff = deriveEntitlementDiff(original, entitlementIds);
        const payload = correctionToPayload(
          accountId,
          entitlementIds,
          newEntitlementNames,
        );
        return {
          ...payload,
          accountLabel: labelById.get(accountId) ?? "this account",
          addedNames: [
            ...diff.added.map((id) => nameById.get(id) ?? id),
            ...payload.newEntitlementNames,
          ],
          removedNames: diff.removed.map((id) => nameById.get(id) ?? id),
        };
      })
      .filter(
        (c) =>
          c.addedNames.length > 0 ||
          c.removedNames.length > 0 ||
          c.newEntitlementNames.length > 0,
      );
  };

  const handleApply = async () => {
    if (isLocked) return;
    const corrections = isBulk ? stageBulk() : stageSingle();
    if (corrections.length === 0) {
      onClose();
      return;
    }
    setIsApplying(true);
    try {
      await onApply(corrections);
      onClose();
    } finally {
      setIsApplying(false);
    }
  };

  const subtitle = isBulk
    ? `Fixing recorded access for ${selectedAccounts.length} accounts`
    : `Fixing ${accountLabel(selectedAccounts[0] || ({} as MFReviewAccount))}'s recorded access`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay bg="rgba(0, 0, 0, 0.2)" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="12px" boxShadow="lg" overflow="hidden">
        <ModalHeader
          borderBottom="1px solid"
          borderColor="border.default"
          py={4}
          px={5}
        >
          <HStack spacing="sm" align="center">
            <Icon as={AlertTriangle} boxSize="18px" color="text.body.secondary" />
            <VStack align="flex-start" spacing={0} minW={0}>
              <Text fontSize="15px" fontWeight="600" color="text.body.primary">
                Fix incorrect data
              </Text>
              <Text fontSize="13px" color="text.body.tertiary" noOfLines={1}>
                {subtitle}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton top={3} right={3} />

        <ModalBody py={4} px={5}>
          {isLoadingOptions ? (
            <Flex py={10} justify="center" align="center" gap={2}>
              <Spinner size="sm" color="text.body.tertiary" speed="0.8s" />
              <Text fontSize="13px" color="text.body.tertiary">
                Loading…
              </Text>
            </Flex>
          ) : (
            <VStack spacing={4} align="stretch">
              <Box
                bg="bg.warning"
                border="1px solid"
                borderColor="warning.200"
                borderRadius="8px"
                px={3}
                py={2}
              >
                <Text fontSize="12px" color="warning.800" lineHeight="1.5">
                  Fixes apply immediately to Ploy&rsquo;s record. This is not a
                  review decision.
                </Text>
              </Box>
              <Text fontSize="13px" color="text.body.secondary" lineHeight="1.5">
                {isBulk
                  ? "Tick the entitlements these accounts actually have at source. A partial tick means it's mixed across the selection: leave it to keep each account as-is."
                  : "Tick the entitlements this account actually has at source."}
              </Text>

              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <Icon as={Search} boxSize="14px" color="text.body.tertiary" />
                </InputLeftElement>
                <Input
                  placeholder="Search or type to create…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderRadius="6px"
                  fontSize="13px"
                  h="34px"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !hasExactMatch) {
                      e.preventDefault();
                      handleCreateEntitlement();
                    }
                  }}
                />
              </InputGroup>

              <Box
                maxHeight="320px"
                overflowY="auto"
                sx={{
                  "&::-webkit-scrollbar": { width: "4px" },
                  "&::-webkit-scrollbar-thumb": {
                    bg: "border.default",
                    borderRadius: "4px",
                  },
                }}
              >
                <VStack align="stretch" spacing={3}>
                  {groups.length === 0 && !searchQuery.trim() && (
                    <Text
                      fontSize="13px"
                      color="text.body.tertiary"
                      textAlign="center"
                      py={6}
                    >
                      No entitlements for this resource
                    </Text>
                  )}

                  {groups.map((group) => {
                    const visibleItems = group.items.filter(matches);
                    if (visibleItems.length === 0) return null;
                    return (
                      <VStack key={group.category} align="stretch" spacing="2xs">
                        <Text
                          fontSize="11px"
                          fontWeight={600}
                          color="text.body.tertiary"
                          textTransform="uppercase"
                          letterSpacing="0.04em"
                          px={1}
                        >
                          {group.category}
                        </Text>
                        {visibleItems.map((option) => {
                          const st = isBulk ? bulkState[option.id] : undefined;
                          const checkedSingle = picked.includes(option.id);
                          const wasHeld = isBulk
                            ? originalBulkState[option.id]
                            : currentEntitlementIds(
                                selectedAccounts[0] ||
                                  ({} as MFReviewAccount),
                              ).includes(option.id);
                          const adding = isBulk
                            ? st !== originalBulkState[option.id] &&
                              st === "checked"
                            : checkedSingle && !wasHeld;
                          const removing = isBulk
                            ? st !== originalBulkState[option.id] &&
                              st === "unchecked"
                            : !checkedSingle && wasHeld;
                          const showMixed = isBulk && st === "mixed";
                          return (
                            <Flex
                              key={option.id}
                              py="8px"
                              px={3}
                              align="center"
                              gap={3}
                              borderWidth="1px"
                              borderColor="border.default"
                              borderRadius="8px"
                              cursor="pointer"
                              _hover={{ bg: "bg.section" }}
                              onClick={() =>
                                isBulk
                                  ? toggleBulk(option.id)
                                  : toggleSingle(option.id)
                              }
                            >
                              <Checkbox
                                isChecked={isBulk ? st === "checked" : checkedSingle}
                                isIndeterminate={showMixed}
                                pointerEvents="none"
                                size="sm"
                              />
                              <Text
                                fontSize="13px"
                                fontWeight="450"
                                color="text.body.primary"
                                flex={1}
                                noOfLines={1}
                              >
                                {option.name}
                              </Text>
                              {adding && (
                                <Tag size="sm" colorScheme="purple" variant="subtle">
                                  {isBulk ? "Adding to all" : "Adding"}
                                </Tag>
                              )}
                              {removing && (
                                <Tag size="sm" colorScheme="red" variant="subtle">
                                  {isBulk ? "Removing from all" : "Removing"}
                                </Tag>
                              )}
                              {showMixed && (
                                <Tag size="sm" colorScheme="gray" variant="subtle">
                                  Mixed
                                </Tag>
                              )}
                            </Flex>
                          );
                        })}
                      </VStack>
                    );
                  })}

                  {newEntitlementNames.map((name) => (
                    <Flex
                      key={`new-${name}`}
                      py="8px"
                      px={3}
                      align="center"
                      gap={3}
                      borderWidth="1px"
                      borderColor="p.200"
                      borderRadius="8px"
                      bg="bg.azurite"
                    >
                      <Checkbox isChecked pointerEvents="none" size="sm" />
                      <Text
                        fontSize="13px"
                        fontWeight="450"
                        color="text.body.primary"
                        flex={1}
                        noOfLines={1}
                      >
                        {name}
                      </Text>
                      <Tag size="sm" colorScheme="purple" variant="subtle">
                        New
                      </Tag>
                    </Flex>
                  ))}

                  {searchQuery.trim() && !hasExactMatch && (
                    <Flex
                      py="8px"
                      px={3}
                      align="center"
                      gap={2}
                      borderRadius="8px"
                      _hover={{ bg: "bg.section" }}
                      cursor="pointer"
                      onClick={handleCreateEntitlement}
                    >
                      <Flex
                        w="16px"
                        h="16px"
                        align="center"
                        justify="center"
                        borderRadius="3px"
                        border="1px dashed"
                        borderColor="border.strong"
                      >
                        <Icon as={Plus} boxSize="10px" color="text.body.secondary" />
                      </Flex>
                      <Text fontSize="13px" color="text.body.secondary" ml={1}>
                        Create{" "}
                        <Text as="span" fontWeight="500" color="text.body.primary">
                          {searchQuery.trim()}
                        </Text>
                      </Text>
                    </Flex>
                  )}
                </VStack>
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter
          borderTop="1px solid"
          borderColor="border.default"
          py={3}
          px={5}
          gap={2}
        >
          <Button
            variant="subtle"
            tone="neutral"
            onClick={onClose}
            size="md"
            isDisabled={isApplying}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            tone="primary"
            onClick={handleApply}
            isLoading={isApplying}
            isDisabled={isLoadingOptions || !changed || isLocked}
            size="md"
          >
            Apply fixes
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FixIncorrectDataModal;
